import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { User } from 'anchor-shared/lib/model/entity/user';
import { UserDAO } from '../../../../../core/services/dao/userDAO';
import { ConstantMan } from '../../../../../core/services/constantMan';
import { RoleDAO } from '../../../../../core/services/dao/roleDAO';
import { Role } from 'anchor-shared/lib/model/entity/role';
import { ApiHelper } from '../../../../../core/services/apiHelper';
import { Router } from '@angular/router';
import { AdminAccess } from '../../../../shared/accessAdmin';
import { Subject, Subscription } from 'rxjs';
import * as moment from 'moment';
import { NumberFormatterService } from '../../../../../core/services/number.formatter.service';
import { NgForm } from '@angular/forms';
import { ContainsSubscriptions } from '../../../../../core/services/interfaces/contains-subscriptions';
import { unsubscribeFromAll } from '../../../../../core/pure-functions/rx-helper';
import * as intlTelInput from 'intl-tel-input';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
})
export class UserFormComponent
  implements OnInit, OnDestroy, ContainsSubscriptions, AfterViewInit
{
  @ViewChild('intlTelInput') private _inputElement!: ElementRef;
  private _intlTelInput: any;
  public E164PhoneNumber!: string | null;
  @Input() title: string;

  @Input() user: User;

  @Input() newUser: boolean;

  @Input() adminAccess: boolean;

  @Input() enablePushToDataLayer: boolean;

  @Input() userRoles: Role[];

  @Input() hideCancelButton: boolean = false;

  @Output() onCancelled = new EventEmitter<boolean>();
  @Output() onSubmitted = new EventEmitter<any>();

  subs: Subscription[] = [];
  submitClicked = false;
  public model: any;
  public userStatus: string[] = [];
  public roles: Role[];
  public startDate: any;
  public maxDate: any;
  public birthdate: any;
  public toVerifyEmail: Subject<string> = new Subject<string>();
  public dateMask = [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/];
  public userIs18 = false;
  @ViewChild('userForm') userForm: NgForm;

  constructor(
    private userDAO: UserDAO,
    private roleDAO: RoleDAO,
    private apiHelper: ApiHelper,
    private router: Router,
    private adminCheck: AdminAccess,
    private numFormatter: NumberFormatterService,
  ) {
    this.startDate = moment().subtract(18, 'years');
    this.maxDate = moment().subtract(18, 'years');
    this.maxDate.add(1, 'days');

    if (this.adminAccess) {
      this.adminCheck.checkAdminAccess(
        this.userDAO,
        this.apiHelper,
        this.router,
      );
    }
  }

  ngOnInit() {
    for (status in ConstantMan.STATUS_TYPE) {
      this.userStatus.push(ConstantMan.STATUS_TYPE[status]);
    }
    if (this.newUser) {
      this.model = this.user;
      if (this.adminAccess) {
        const rolesSub = this.roleDAO.get().subscribe((roles) => {
          this.roles = roles;
        });
        this.subs.push(rolesSub);
      }
    } else {
      this.birthdate = moment(this.user.birthday).format('MM/DD/YYYY');
      this.model = this.user;
      if (this.adminAccess) {
        if (this.userRoles.length > 0) {
          const rolesSub = this.roleDAO
            .getAllRolesNotAssociatedToUserId(this.user.id)
            .subscribe((roles) => {
              this.roles = roles;
            });
          this.subs.push(rolesSub);
        } else {
          const rolesSub = this.roleDAO.get().subscribe((roles) => {
            this.roles = roles;
          });
          this.subs.push(rolesSub);
        }
      }
    }
  }

  ngAfterViewInit(): void {
    const intlTelInputInstance = intlTelInput;
    setTimeout(() => {
      this._intlTelInput = intlTelInputInstance(
        this._inputElement.nativeElement,
        { preferredCountries: ['us', 'mx'] },
      );
    }, 1000);
  }

  get intlTelInput(): any {
    return this._intlTelInput;
  }

  get internationalPhoneNumber(): string {
    return this.model.phoneNumber;
  }

  set internationalPhoneNumber(value: string) {
    if (!!value) {
      this._intlTelInput.setNumber(value);
    }
    this.model.phoneNumber = value;
    this.i18nizePhoneNumber();
  }

  public i18nizePhoneNumber(): void {
    this.E164PhoneNumber = null;
    if (this._intlTelInput.isValidNumber()) {
      this.E164PhoneNumber = this._intlTelInput.getNumber();
    }
  }

  hasInvalidPhoneNumber(): boolean {
    if (this._intlTelInput && this._intlTelInput.isValidNumber()) {
      return false;
    }
    return true;
  }

  checkAge(evt) {
    this.userIs18 = this.numFormatter.isDate18orMoreYearsOld(evt);
    if (!this.userIs18) {
      this.userForm.form.controls.birthday.setErrors({ valid: this.userIs18 });
    }
    this.pushToDataLayer();
  }

  cancel() {
    this.onCancelled.emit(true);
  }

  validForm(userForm: any) {
    if (this.adminAccess) {
      if (!userForm.valid && this.userRoles.length === 0) {
        return false;
      } else {
        return userForm.valid;
      }
    } else {
      return userForm.valid;
    }
  }

  onSubmit() {
    if (!this.submitClicked) {
      this.model.phoneNumber = this.E164PhoneNumber;
      this.submitClicked = true;
      const userRoles: number[] = [];
      this.model.birthday = this.numFormatter.getDateFromDateString(
        this.birthdate,
      );
      const userForm = {
        user: this.model,
        roles: userRoles,
      };
      if (this.adminAccess) {
        this.userRoles.forEach((model: Role, index: number) => {
          userRoles.push(model.id);
        });
        userForm.roles = userRoles;
        userForm.user.password = null;
      }
      this.pushToDataLayer();
      this.onSubmitted.emit(userForm);
    }
  }

  public emailBlurred(emailString: string) {
    this.toVerifyEmail.next(emailString); // add to subject string, verification handled through [enrollEmailDuplicate] directive
  }

  public emailKeyupHandler(emailString: string) {
    this.toVerifyEmail.next(emailString); // add to subject string, verification handled through [enrollEmailDuplicate] directive
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  public pushToDataLayer() {
    if (this.enablePushToDataLayer) {
      const shajs = require('sha.js');
      const em = this.model.email ? this.model.email : 'no@email.com';
      const ph = this.model.phoneNumber
        ? this.model.phoneNumber
        : '+11112223333';
      const fn = this.model.firstName ? this.model.firstName : 'firstName';
      const ln = this.model.lastName ? this.model.lastName : 'lastName';
      const db = this.model.birthday ? this.model.birthday : '00000000';
      window['dataLayer'].push({
        em_unhashed: em,
        ph_unhashed: ph,
        fn_unhashed: fn,
        ln_unhashed: ln,
        db_unhashed: db,
        em: shajs('sha256').update(em).digest('hex'),
        ph: shajs('sha256').update(ph).digest('hex'),
        fn: shajs('sha256').update(fn).digest('hex'),
        ln: shajs('sha256').update(ln).digest('hex'),
        db: shajs('sha256').update(db).digest('hex'),
      });
    }
  }
}
